import { useCallback, useState } from 'react';
import { useCurrentUserId } from '@framework/auth';
import type { WorkspaceEntity, WorkspaceMemberRoles } from '@workspace/domain/workspace';
import { MemberAddModal } from './MemberAddModal';
import { MemberEditModal } from './MemberEditModal';
import { MemberRemoveModal } from './MemberRemoveModal';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { usePrimitiveValueListener } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import type { GroupMemberRolesJSON, WorkspaceInternalPublicMemberRoleJSON } from 'balus-common';

type TargetModal = 'Add' | 'Edit' | 'Remove';

type Props = {
  isOpen: boolean;
  onClose(): void;
  workspace: WorkspaceEntity;
  memberRoles: WorkspaceMemberRoles;
};

export const WorkspaceMemberEditModal: React.FC<Props> = ({ isOpen, onClose, workspace, memberRoles }: Props) => {
  const currentUserId = useCurrentUserId();
  const [targetModal, setTargetModal] = useState<TargetModal>('Edit');

  const [internalPublicSetting] = usePrimitiveValueListener<WorkspaceInternalPublicMemberRoleJSON>(
    RTDBPath.Group.internalPublicWorkspacePath(workspace.groupId, workspace.id)
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleMemberAddStart = useCallback(() => setTargetModal('Add'), []);
  const handleMemberRemoveStart = useCallback(() => setTargetModal('Remove'), []);
  const handleCancel = useCallback(() => setTargetModal('Edit'), []);

  const [groupMemberRoles] = useSnapshot<GroupMemberRolesJSON>({
    path: RTDBPath.Group.memberRolesPath(workspace.groupId),
    load: ({ snapshot }) => snapshot.val() || {},
  });

  if (!currentUserId) return null;
  if (!isOpen) return null;

  return (
    <>
      <MemberEditModal
        isOpen={targetModal === 'Edit'}
        onClose={handleClose}
        workspace={workspace}
        internalPublicSetting={internalPublicSetting}
        memberRoles={memberRoles}
        groupMemberRoles={groupMemberRoles || {}}
        currentUserId={currentUserId}
        onMemberAdd={handleMemberAddStart}
        onMemberRemove={handleMemberRemoveStart}
      />

      <MemberRemoveModal
        isOpen={targetModal === 'Remove'}
        onClose={handleCancel}
        workspace={workspace}
        internalPublicSetting={internalPublicSetting}
        memberRoles={memberRoles}
        groupMemberRoles={groupMemberRoles || {}}
        currentUserId={currentUserId}
      />

      <MemberAddModal
        isOpen={targetModal === 'Add'}
        onClose={handleCancel}
        workspace={workspace}
        memberRoles={memberRoles}
        groupMemberRoles={groupMemberRoles || {}}
      />
    </>
  );
};
